import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, LogOut, Settings, UserPlus } from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import { recruitmentService } from '@/services/recruitment.service';
import { cn } from '@/lib/utils';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { Button } from '@/components/ui/button';
import { Tabs } from '@/components/ui/tabs';
import { PreJoiningTab } from '@/components/recruitment/PreJoiningTab';
import { ProcessTab } from '@/components/recruitment/ProcessTab';

type OuterTab = 'pre' | 'post' | 'exit';

export function RecruitmentPage() {
  const [tab, setTab] = useState<OuterTab>('pre');
  const statsQuery = useQuery({ queryKey: ['recruitment', 'stats'], queryFn: () => recruitmentService.stats() });

  const joining = statsQuery.data?.joining;
  const exit = statsQuery.data?.exit;
  const postCount = joining ? joining.in_progress + joining.completed : undefined;
  const exitCount = exit ? exit.in_progress + exit.completed : undefined;

  const tabConfig: { key: OuterTab; label: string; icon: typeof Briefcase; count?: number }[] = [
    { key: 'pre', label: 'Pre-Joining', icon: Briefcase },
    { key: 'post', label: 'Post-Joining', icon: UserPlus, count: postCount },
    { key: 'exit', label: 'Exit', icon: LogOut, count: exitCount },
  ];

  const tabs = tabConfig.map((t) => {
    const Icon = t.icon;
    return {
      key: t.key,
      label: (
        <span className="inline-flex items-center gap-2">
          <Icon className="h-4 w-4" />
          {t.label}
          {t.count !== undefined && (
            <span className={cn(
              'rounded-full px-1.5 py-0.5 text-xs font-semibold leading-none',
              tab === t.key ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground',
            )}>
              {t.count}
            </span>
          )}
        </span>
      ),
    };
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Recruitment Process</h1>
          <p className="text-sm text-muted-foreground">Track candidate pipeline, employee joining &amp; exit processes</p>
        </div>
        <PermissionGate permission={PERMISSIONS.RECRUITMENT_CONFIG}>
          <Button asChild variant="outline">
            <Link to="/hrms/config?tab=recruitment"><Settings className="h-4 w-4" /> Configure</Link>
          </Button>
        </PermissionGate>
      </div>

      <Tabs tabs={tabs} active={tab} onChange={(k) => setTab(k as OuterTab)} />

      {tab === 'pre' && <PreJoiningTab />}
      {tab === 'post' && <ProcessTab processType="joining" />}
      {tab === 'exit' && <ProcessTab processType="exit" />}
    </div>
  );
}

export default RecruitmentPage;
